import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, SessionExpiryRedirect } from './context/AuthContext';
import { PlatformAuthProvider, usePlatformAuth } from './context/PlatformAuthContext';
import PushBanner from './components/PushBanner';
import ConsentOnboardingSheet from './components/ConsentOnboardingSheet';
import AnalyticsGate from './components/AnalyticsGate';
import './index.css';

import ClubHub from './pages/ClubHub';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ConfirmEmailPage from './pages/ConfirmEmailPage';
import NotMePage from './pages/NotMePage';
import EmailConfirmGate from './components/EmailConfirmGate';
import AdminPage from './pages/AdminPage';
import KitShopPage from './pages/KitShopPage';
import SponsorsPage from './pages/SponsorsPage';
import QRWalletPage from './pages/QRWalletPage';
import InviteAcceptPage from './pages/InviteAcceptPage';
import CoparentAcceptPage from './pages/CoparentAcceptPage';
import MyAccountPage from './pages/MyAccountPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import MyClubsPage from './pages/MyClubsPage';
import FindClubPage from './pages/FindClubPage'; // B-58 Find your club
import CreateClubPage from './pages/CreateClubPage';
import ReportProblemPage from './pages/ReportProblemPage';
import SponsorScanPage from './pages/SponsorScanPage';
import SponsorAttributionPage from './pages/SponsorAttributionPage';
import SponsorSettingsPage from './pages/SponsorSettingsPage';
import ScanCardsPage from './pages/ScanCardsPage';
import WalletRedirect from './pages/WalletRedirect';
import DeepLinkListener from './native/DeepLinkListener';
import ScrollToTop from './components/layout/ScrollToTop';

import PlatformLogin from './pages/PlatformLogin';
import PlatformDashboard from './pages/platform/PlatformDashboard';
import PlatformClubs from './pages/platform/PlatformClubs';
import PlatformStaff from './pages/platform/PlatformStaff';
import PlatformSponsors from './pages/platform/PlatformSponsors';
import PlatformReports from './pages/platform/PlatformReports';
import PlatformSupport from './pages/platform/PlatformSupport';
import PlatformPush from './pages/platform/PlatformPush';
import PlatformDoc from './pages/platform/PlatformDoc';
import SharedLinkRedirect from './pages/SharedLinkRedirect';
import ToursPage from './pages/ToursPage'; // B-44.1 Tours & festivals
import ClubPauseGate from './components/ClubPauseGate'; // B-38 item 4: a suspended club is locked

// "/" goes straight to login (or /my-clubs if a session already exists) on
// the web too (Stuart, 1 Oct: the marketing landing page was just an extra
// click before logging in). The native shell has always done this.
// LandingPage.jsx is kept, unrouted, in case a marketing site wants it.
import { useAuth as useAuthForGate } from './context/AuthContext';
import LazyPageBoundary from './components/LazyPageBoundary';

// Club switcher (30 Oct): going from one club's page straight to the same
// page of another club keeps the route mounted, so a fresh page per club
// (the hub's tab / team filter / refs; the sponsor till's code in hand).
const KeyBySlug = ({ children }) => { const { clubSlug } = useParams(); return <React.Fragment key={clubSlug}>{children}</React.Fragment>; };

// B-50 Phase 0: staff-only ad preview, split out of the main bundle (spec §11:
// "never in the app bundle") — only staff who open the page download it.
const PlatformAdPreview = React.lazy(() => import('./pages/platform/PlatformAdPreview'));

const HomeGate = () => {
  const { user, loading } = useAuthForGate();
  // WT-1 review: a spinner, not a blank page, while a stored session loads
  // (or waits on signal, with the reconnect notice underneath).
  if (loading) return <div className="page-loading"><div className="spinner" style={{ width: 40, height: 40 }} /></div>;
  return <Navigate to={user ? '/my-clubs' : '/login'} replace />;
};

// Force favicon — overwrites whatever index.html cached version says
const existingFavicon = document.querySelector('link[rel="icon"]');
if (existingFavicon) existingFavicon.href = '/marakin-icon.png';
else {
  const link = document.createElement('link');
  link.rel = 'icon'; link.type = 'image/png'; link.href = '/marakin-icon.png';
  document.head.appendChild(link);
}

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1, staleTime: 30000 } } });

// Protected route for platform
function PlatformProtectedRoute({ children }) {
  const { platformUser, loading } = usePlatformAuth();

  if (loading) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        background: 'var(--charcoal, #2C2B28)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(244,239,230,0.18)',
          borderTopColor: 'var(--gold, #C4943A)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
      </div>
    );
  }

  if (!platformUser) {
    return <Navigate to="/marakin-admin/login" replace />;
  }

  return children;
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <PlatformAuthProvider>
          <BrowserRouter>
            {/* B-56: a new page opens at the top (not on Back, not for ?tab= or #anchors) */}
            <ScrollToTop />
            {/* W-10: expired JWT → friendly re-login with return path */}
            <SessionExpiryRedirect />
            {/* Native only: App Links / Universal Links + the card shortcut route into the SPA */}
            <DeepLinkListener />
            {/* In-app banner for pushes received while the app is open (foreground) */}
            <PushBanner />
            {/* One-time consent sheet for logged-in users who've never been asked */}
            <ConsentOnboardingSheet />
            {/* Consent-gated PostHog product analytics + session replay — renders nothing */}
            <AnalyticsGate />
            {/* B-65: an unconfirmed email sees "Check your inbox" instead of the app (frontend-only gate; see the component) */}
            <EmailConfirmGate>
            <Routes>
              <Route path="/" element={<HomeGate />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/delete-account" element={<DeleteAccountPage />} />
              <Route path="/my-clubs" element={<MyClubsPage />} />
              <Route path="/find-club" element={<FindClubPage />} />
              <Route path="/wallet" element={<WalletRedirect />} />
              <Route path="/create-club" element={<CreateClubPage />} />
              <Route path="/report-problem" element={<ReportProblemPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/confirm-email/:token" element={<ConfirmEmailPage />} />
              <Route path="/not-me/:token" element={<NotMePage />} />
              {/* B-38 item 4: every club page sits behind ClubPauseGate ("This club is
                  paused" for a suspended club; staff see it as normal). Not
                  report-problem: that's how a paused club contacts Marakin. */}
              <Route path="/:clubSlug" element={<ClubPauseGate><KeyBySlug><ClubHub /></KeyBySlug></ClubPauseGate>} />
              <Route path="/:clubSlug/shop" element={<ClubPauseGate><KitShopPage /></ClubPauseGate>} />
              <Route path="/:clubSlug/sponsors" element={<ClubPauseGate><SponsorsPage /></ClubPauseGate>} />
              <Route path="/:clubSlug/wallet" element={<ClubPauseGate><QRWalletPage /></ClubPauseGate>} />
              <Route path="/:clubSlug/scan-cards" element={<ClubPauseGate><ScanCardsPage /></ClubPauseGate>} />
              <Route path="/:clubSlug/admin" element={<ClubPauseGate><AdminPage /></ClubPauseGate>} />
              <Route path="/:clubSlug/account" element={<ClubPauseGate><MyAccountPage /></ClubPauseGate>} />
              <Route path="/:clubSlug/report-problem" element={<ReportProblemPage />} />
              <Route path="/:clubSlug/invite/:token" element={<ClubPauseGate><InviteAcceptPage /></ClubPauseGate>} />
              {/* Share to WhatsApp: short links the backend previews + the apps claim */}
              <Route path="/:clubSlug/e/:id" element={<ClubPauseGate><SharedLinkRedirect kind="event" /></ClubPauseGate>} />
              <Route path="/:clubSlug/n/:id" element={<ClubPauseGate><SharedLinkRedirect kind="news" /></ClubPauseGate>} />
              <Route path="/:clubSlug/coparent-accept/:token" element={<ClubPauseGate><CoparentAcceptPage /></ClubPauseGate>} />
              {/* B-44.1 Find a tour: rugby club staff only (the page checks) */}
              <Route path="/:clubSlug/tours" element={<ClubPauseGate><ToursPage /></ClubPauseGate>} />

              {/* Sponsor pages — within club context, uses regular auth */}
              <Route path="/:clubSlug/scan" element={<ClubPauseGate><KeyBySlug><SponsorScanPage /></KeyBySlug></ClubPauseGate>} />
              <Route path="/:clubSlug/attribution" element={<ClubPauseGate><SponsorAttributionPage /></ClubPauseGate>} />
              <Route path="/:clubSlug/sponsor-settings" element={<ClubPauseGate><SponsorSettingsPage /></ClubPauseGate>} />

              {/* Platform Admin Routes */}
              <Route path="/marakin-admin/login" element={<PlatformLogin />} />
              <Route path="/marakin-admin" element={<PlatformProtectedRoute><PlatformDashboard /></PlatformProtectedRoute>} />
              <Route path="/marakin-admin/clubs" element={<PlatformProtectedRoute><PlatformClubs /></PlatformProtectedRoute>} />
              <Route path="/marakin-admin/sponsors" element={<PlatformProtectedRoute><PlatformSponsors /></PlatformProtectedRoute>} />
              <Route path="/marakin-admin/ad-preview" element={<PlatformProtectedRoute><LazyPageBoundary><React.Suspense fallback={null}><PlatformAdPreview /></React.Suspense></LazyPageBoundary></PlatformProtectedRoute>} />
              <Route path="/marakin-admin/reports" element={<PlatformProtectedRoute><PlatformReports /></PlatformProtectedRoute>} />
              <Route path="/marakin-admin/support" element={<PlatformProtectedRoute><PlatformSupport /></PlatformProtectedRoute>} />
              <Route path="/marakin-admin/push" element={<PlatformProtectedRoute><PlatformPush /></PlatformProtectedRoute>} />
              <Route path="/marakin-admin/staff" element={<PlatformProtectedRoute><PlatformStaff /></PlatformProtectedRoute>} />
              <Route path="/marakin-admin/roadmap" element={<PlatformProtectedRoute><PlatformDoc doc="roadmap" title="Product Roadmap" /></PlatformProtectedRoute>} />
              <Route path="/marakin-admin/bugs-tweaks" element={<PlatformProtectedRoute><PlatformDoc doc="bugs-tweaks" title="Bugs & Tweaks" /></PlatformProtectedRoute>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </EmailConfirmGate>
          </BrowserRouter>
        </PlatformAuthProvider>
      </AuthProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
